import React, { useState } from "react";
import { X, AlertCircle } from "lucide-react";

const WineBulkActionModal = ({ isOpen, onClose, onConfirm, selectedCount }) => {
  const [activeCategory, setActiveCategory] = useState("Status");
  const [selectedAction, setSelectedAction] = useState("");
  const [actionValue, setActionValue] = useState(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  if (!isOpen) return null;

  const categories = [
    {
      name: "Status",
      actions: [
        "Set Status → Active",
        "Set Status → Inactive",
        "Archive Wines",
      ],
    },
    {
      name: "Offering",
      actions: ["Set Offering"],
    },
    {
      name: "Pricing",
      actions: ["Set Glass Price", "Set Bottle Price"],
    },
    {
      name: "Category & Style",
      actions: ["Change Category & Style"],
    },
    {
      name: "Characteristics",
      actions: [
        "Toggle Filtered",
        "Set Filtered → Yes",
        "Set Filtered → No",
        "Toggle Residual Sugar",
        "Set Residual Sugar → Yes",
        "Set Residual Sugar → No",
        "Toggle Organic",
        "Set Organic → Yes",
        "Set Organic → No",
        "Toggle Biodynamic",
        "Set Biodynamic → Yes",
        "Set Biodynamic → No",
        "Toggle Vegan",
        "Set Vegan → Yes",
        "Set Vegan → No",
      ],
    },
    {
      name: "Notes",
      actions: ["Add Notes", "Replace Notes", "Clear Notes"],
    },
    {
      name: "Vintage",
      actions: ["Update Vintage"],
    },
    {
      name: "Varietals",
      actions: [
        "Add Varietal(s)",
        "Remove Varietal(s)",
        "Replace Varietal List",
      ],
    },
  ];

  const wineCategories = [
    "Sparkling",
    "White",
    "Rose",
    "Red",
    "Orange",
    "Dessert",
  ];

  const styleStructure = {
    "Sparkling": ["Fruit Driven Sparkling", "Complex Sparkling"],
    "White": ["Fresh, Unoaked White", "Earthy White", "Aromatic White", "Rich, Oaky White"],
    "Rose": ["Blush Rosé", "Dry Rosé"],
    "Red": ["Mild, Mannered Red", "Soft, Fruity Red", "Fresh, Spicy Red", "Powerful Red"],
    "Orange": ["Aromatic Orange", "Fruity Orange", "Rich Orange"],
    "Dessert": ["Sparkling Dessert", "Subtle, Aromatic Dessert", "Rich, Fruity Dessert"]
  };

  const handleActionSelect = (action) => {
    setSelectedAction(action);
    if (action === "Replace Notes" || action === "Add Notes" || action === "Add Varietal(s)" || action === "Remove Varietal(s)" || action === "Replace Varietal List") {
      setActionValue("");
    } else {
      setActionValue(null);
    }
    setIsConfirming(false);
  };

  const handleConfirm = async () => {
    if (!isConfirming) {
      setIsConfirming(true);
    } else {
      setIsLoading(true);
      try {
        await onConfirm(selectedAction, actionValue);
        onClose();
      } catch (error) {
        console.error("Bulk action failed:", error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const renderActionInput = () => {
    switch (selectedAction) {
      case "Set Offering":
        return (
          <div className="grid grid-cols-1 gap-2 mt-4">
            {["By the Glass (BTG)", "By the Bottle (BTB)", "BTG + BTB"].map(
              (opt) => (
                <label
                  key={opt}
                  className="flex items-center gap-2 text-sm p-3 border border-gray-100 rounded hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="radio"
                    name="offering"
                    checked={actionValue === opt}
                    onChange={() => setActionValue(opt)}
                  />
                  {opt}
                </label>
              )
            )}
          </div>
        );
      case "Set Glass Price":
      case "Set Bottle Price":
        return (
          <div className="mt-4">
            <label className="text-sm text-gray-500 block mb-1">
              Set Price (numbers only):
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                $
              </span>
              <input
                type="number"
                step="0.01"
                className="w-full p-2 pl-7 border border-gray-300 rounded-md"
                placeholder="0.00"
                value={actionValue || ""}
                onChange={(e) => setActionValue(e.target.value)}
              />
            </div>
          </div>
        );
      case "Change Category & Style":
        return (
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-bold text-gray-700 block mb-1">
                Category
              </label>
              <select
                className="w-full p-2 border border-gray-300 rounded-md"
                value={actionValue?.category || ""}
                onChange={(e) =>
                  setActionValue({
                    ...actionValue,
                    category: e.target.value,
                    style: "",
                  })
                }
              >
                <option value="">Select Category</option>
                {wineCategories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>
            {actionValue?.category && (
              <div>
                <label className="text-sm font-bold text-gray-700 block mb-1">
                  Wine Style
                </label>
                <select
                  className="w-full p-2 border border-gray-300 rounded-md"
                  value={actionValue?.style || ""}
                  onChange={(e) =>
                    setActionValue({ ...actionValue, style: e.target.value })
                  }
                >
                  <option value="">Select Style</option>
                  {styleStructure[actionValue.category]?.map((style) => (
                    <option key={style} value={style}>
                      {style}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        );
      case "Add Notes":
      case "Replace Notes":
        return (
          <textarea
            className="w-full p-2 border border-gray-300 rounded-md mt-4 h-24"
            placeholder="Enter notes..."
            value={actionValue || ""}
            onChange={(e) => setActionValue(e.target.value)}
          />
        );
      case "Update Vintage":
        return (
          <div className="mt-4">
            <label className="text-sm text-gray-500 block mb-1">
              Enter Vintage (YYYY or NV):
            </label>
            <input
              type="text"
              className="w-full p-2 border border-gray-300 rounded-md"
              placeholder="e.g. 2021, NV"
              value={actionValue || ""}
              onChange={(e) => setActionValue(e.target.value)}
            />
          </div>
        );
      case "Add Varietal(s)":
      case "Remove Varietal(s)":
      case "Replace Varietal List":
        return (
          <div className="mt-4">
            <p className="text-sm text-gray-500 mb-2">
              Enter varietals separated by commas:
            </p>
            <input
              type="text"
              className="w-full p-2 border border-gray-300 rounded-md"
              placeholder="e.g. Cabernet Sauvignon, Merlot"
              value={actionValue || ""}
              onChange={(e) => setActionValue(e.target.value)}
            />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="p-4 border-b border-gray-100 flex items-center justify-between bg-white sticky top-0 z-10">
          <div>
            <h2 className="text-xl font-bold text-[#881337]">
              Bulk Actions - Wine Menu Items
            </h2>
            <p className="text-sm text-gray-500 font-medium">
              {selectedCount} wines selected
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X size={20} className="text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto flex">
          {/* Categories Sidebar */}
          <div className="w-48 bg-gray-50 border-r border-gray-100">
            {categories.map((cat) => (
              <button
                key={cat.name}
                onClick={() => {
                  setActiveCategory(cat.name);
                  setSelectedAction("");
                  setActionValue(null);
                  setIsConfirming(false);
                }}
                className={`w-full text-left px-4 py-3 text-sm font-semibold transition-colors ${activeCategory === cat.name
                  ? "bg-white text-[#881337] border-r-2 border-[#881337]"
                  : "text-gray-600 hover:bg-gray-100"
                  }`}
              >
                {cat.name}
              </button>
            ))}
          </div>

          {/* Actions Pane */}
          <div className="flex-1 p-6">
            {!selectedAction ? (
              <div className="space-y-2">
                <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-4">
                  Select an action for {activeCategory}
                </h3>
                {categories
                  .find((c) => c.name === activeCategory)
                  ?.actions.map((action) => (
                    <button
                      key={action}
                      onClick={() => handleActionSelect(action)}
                      className="w-full text-left p-3 rounded-lg border border-gray-200 hover:border-[#881337]/30 hover:bg-[#881337]/5 transition-all group"
                    >
                      <span className="text-sm font-semibold text-gray-700 group-hover:text-[#881337]">
                        {action}
                      </span>
                    </button>
                  ))}
              </div>
            ) : (
              <div className="space-y-4">
                <button
                  onClick={() => {
                    setSelectedAction("");
                    setIsConfirming(false);
                  }}
                  className="text-xs font-bold text-[#881337] hover:underline mb-2 flex items-center gap-1"
                >
                  ← Back to {activeCategory}
                </button>
                <h3 className="text-lg font-bold text-gray-900">
                  {selectedAction}
                </h3>

                {renderActionInput()}

                {isConfirming && (
                  <div className="mt-6 p-4 bg-orange-50 border border-orange-200 rounded-xl flex gap-3">
                    <AlertCircle
                      className="text-orange-500 shrink-0"
                      size={24}
                    />
                    <div>
                      <h4 className="font-bold text-orange-900">
                        Are you sure?
                      </h4>
                      <p className="text-sm text-orange-800">
                        This will apply "{selectedAction}" to {selectedCount}{" "}
                        selected wines. This action cannot be easily undone.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-100 bg-gray-50 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-6 py-2 rounded-lg font-bold text-sm text-gray-500 hover:bg-gray-100 transition-all"
          >
            Cancel
          </button>
          <button
            disabled={
              !selectedAction ||
              ((selectedAction === "Set Glass Price" ||
                selectedAction === "Set Bottle Price") &&
                !actionValue) ||
              (selectedAction === "Change Category & Style" && !actionValue?.category && !actionValue?.style) ||
              (selectedAction === "Replace Notes" && (actionValue === null || actionValue === undefined)) ||
              isLoading
            }
            onClick={handleConfirm}
            className={`px-8 py-2 rounded-lg font-bold text-sm shadow-md transition-all ${!selectedAction
              ? "bg-gray-200 text-gray-400 cursor-not-allowed"
              : isConfirming
                ? "bg-orange-500 text-white hover:bg-orange-600"
                : "bg-[#881337] text-white hover:bg-[#70102d]"
              }`}
          >
            {isLoading ? (
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                <span>Applying...</span>
              </div>
            ) : isConfirming ? (
              "Confirm Apply"
            ) : (
              "Apply Action"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WineBulkActionModal;
